import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { createWorkspaceStore, type WorkspaceStoreConfig, type WorkspaceStore } from './store'
import { WorkspaceStoreContext } from './hooks'

interface WorkspaceStoreProviderProps {
  config: WorkspaceStoreConfig
  children: ReactNode
}

export function WorkspaceStoreProvider({ config, children }: WorkspaceStoreProviderProps) {
  const ref = useRef<{
    store: WorkspaceStore
    destroy: () => void
  } | null>(null)

  if (!ref.current) {
    ref.current = createWorkspaceStore(config)
  }

  // Lifecycle: _init opens BroadcastChannel + visibility listener; _destroy
  // closes them. Both are idempotent (see store.ts) so React StrictMode's
  // double-mount in dev and Vite HMR remounts both re-establish bindings
  // correctly. Without this, the cleanup tears down the broadcast and the
  // remount leaves a dead store — cross-tab sync silently stops working.
  useEffect(() => {
    ref.current!.store.getState()._init()
    return () => {
      ref.current?.destroy()
    }
  }, [])

  return <WorkspaceStoreContext value={ref.current.store}>{children}</WorkspaceStoreContext>
}
